import { useState } from 'react'
import { Check, ChevronDown, Users } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { CONTROLLERS } from '@/mockData'

/**
 * UserFilter Component
 * 
 * A reusable filter component that allows users to filter data by controller.
 * Displays a button with dropdown showing "Everyone" option and individual controllers with initials badges.
 * Matches the exact design from the main Credit Control Dashboard filter.
 * 
 * Features:
 * - Button trigger showing selected filter with icon/avatar
 * - "Everyone" option with Users icon
 * - Individual controllers with colored initials badges
 * - Checkmark on selected option
 * - Separator between "Everyone" and controllers
 * - Consistent spacing, typography, and colors
 * - Dark mode support
 * - Full TypeScript type safety
 * - Accessible (keyboard navigation, screen readers)
 * 
 * @example
 * ```tsx
 * import { UserFilter } from "@/components/filters"
 * import { useState } from "react"
 * 
 * function MyDashboard() {
 *   const [filter, setFilter] = useState('everyone')
 *   
 *   return (
 *     <UserFilter 
 *       value={filter} 
 *       onValueChange={setFilter}
 *     />
 *   )
 * }
 * ```
 */

interface UserFilterProps {
  /** Current selected filter value ('everyone' or controller ID) */
  value: string
  /** Callback when filter value changes */
  onValueChange: (value: string) => void
  /** Optional label for the "everyone" option (default: "Everyone") */
  everyoneLabel?: string
}

// Avatar colors for each controller (matching main dashboard)
const AVATAR_COLORS: Record<string, string> = {
  'user-001': '#3B82F6', // Blue
  'user-002': '#8B5CF6', // Purple
  'user-003': '#EC4899', // Pink
  'user-004': '#F59E0B', // Orange
  'user-005': '#10B981', // Green
  'user-006': '#6366F1', // Indigo
}

export function UserFilter({
  value,
  onValueChange,
  everyoneLabel = 'Everyone'
}: UserFilterProps) {
  const [open, setOpen] = useState(false)

  const handleFilterSelect = (newValue: string) => {
    onValueChange(newValue)
    setOpen(false)
  }

  // Get display info for selected filter
  const selectedController = CONTROLLERS.find(c => c.id === value)
  const isEveryone = value === 'everyone'

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          {isEveryone ? (
            <Users className="h-4 w-4" />
          ) : (
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ backgroundColor: AVATAR_COLORS[value] || '#3B82F6' }}
            >
              {selectedController?.initials}
            </div>
          )}
          <span>{isEveryone ? everyoneLabel : selectedController?.name}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Everyone option */}
        <DropdownMenuItem
          onClick={() => handleFilterSelect('everyone')}
          className="cursor-pointer"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{everyoneLabel}</span>
            </div>
            {value === 'everyone' && (
              <Check className="h-4 w-4" />
            )}
          </div>
        </DropdownMenuItem>
        
        {/* Separator */}
        <DropdownMenuSeparator />
        
        {/* Individual controllers */}
        {CONTROLLERS.map((controller) => (
          <DropdownMenuItem
            key={controller.id}
            onClick={() => handleFilterSelect(controller.id)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                  style={{ backgroundColor: AVATAR_COLORS[controller.id] || '#3B82F6' }}
                >
                  {controller.initials}
                </div>
                <span>{controller.name}</span>
              </div>
              {value === controller.id && (
                <Check className="h-4 w-4" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Get the display name for a filter value
 * @param value - Filter value ('everyone' or controller ID)
 * @param everyoneLabel - Optional custom label for "everyone" (default: "Everyone")
 * @returns Display name for the filter
 * 
 * @example
 * const teamName = getFilterDisplayName('everyone') // Returns "Everyone"
 * const userName = getFilterDisplayName('user-001') // Returns "Sarah Johnson"
 */
export function getFilterDisplayName(value: string, everyoneLabel: string = "Everyone"): string {
  if (value === 'everyone') return everyoneLabel
  const controller = CONTROLLERS.find(c => c.id === value)
  return controller?.name || everyoneLabel
}

/**
 * Get the initials for a filter value
 * @param value - Filter value ('everyone' or controller ID)
 * @returns Initials for the controller, or null for "everyone"
 * 
 * @example
 * const userInitials = getFilterInitials('user-001') // Returns "SJ"
 * const teamInitials = getFilterInitials('everyone') // Returns null
 */
export function getFilterInitials(value: string): string | null {
  if (value === 'everyone') return null
  const controller = CONTROLLERS.find(c => c.id === value)
  return controller?.initials || null
}

/**
 * Check if a filter value represents the team view
 * @param value - Filter value to check
 * @returns True if the filter is "everyone"
 * 
 * @example
 * const isTeam = isTeamFilter('everyone') // Returns true
 * const isTeam = isTeamFilter('user-001') // Returns false
 */
export function isTeamFilter(value: string): boolean {
  return value === 'everyone'
}

/**
 * Check if a filter value represents an individual controller
 * @param value - Filter value to check
 * @returns True if the filter is a controller ID
 * 
 * @example
 * const isTeam = isIndividualFilter('user-001') // Returns true
 * const isTeam = isIndividualFilter('everyone') // Returns false
 */
export function isIndividualFilter(value: string): boolean {
  return value !== 'everyone'
}
